interface ModalDialogOptions {
  onDismiss: () => void | Promise<void>;
  canDismiss?: () => boolean;
  initialFocus?: string;
}

/** Native modality keeps keyboard focus in the active dialog and makes the page inert. */
export function modalDialog(node: HTMLDialogElement, initialOptions: ModalDialogOptions) {
  let options = initialOptions;
  let dismissing = false;
  let startedOutside = false;
  const previousFocus = document.activeElement;

  async function dismiss() {
    if (dismissing || options.canDismiss?.() === false) {
      return;
    }
    dismissing = true;
    try {
      await options.onDismiss();
    } finally {
      dismissing = false;
    }
  }

  function outside(event: MouseEvent) {
    const bounds = node.getBoundingClientRect();
    return (
      event.target === node &&
      (event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom)
    );
  }
  function cancel(event: Event) {
    event.preventDefault();
    void dismiss();
  }
  function pointerdown(event: PointerEvent) {
    startedOutside = outside(event);
  }
  function click(event: MouseEvent) {
    if (startedOutside && outside(event)) {
      void dismiss();
    }
    startedOutside = false;
  }

  function keydown(event: KeyboardEvent) {
    const ignoreKey = event.key !== "Tab" || event.defaultPrevented;
    const outsideDialog =
      !ignoreKey && (!(event.target instanceof Element) || event.target.closest("dialog") !== node);
    if (ignoreKey || outsideDialog) {
      return;
    }
    const controls = [
      ...node.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((control) => control.tabIndex >= 0 && control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls.at(-1);
    if (!first) {
      event.preventDefault();
      node.focus();
      return;
    }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) {
      event.preventDefault();
      last?.focus();
    } else if (
      !event.shiftKey &&
      (document.activeElement === last || document.activeElement === node)
    ) {
      event.preventDefault();
      first.focus();
    }
  }

  node.addEventListener("keydown", keydown);
  node.addEventListener("cancel", cancel);
  node.addEventListener("pointerdown", pointerdown);
  node.addEventListener("click", click);
  node.showModal();
  const selector =
    options.initialFocus ??
    "[autofocus], input:not([disabled]), textarea:not([disabled]), button:not([disabled])";
  node.querySelector<HTMLElement>(selector)?.focus();

  return {
    update(nextOptions: ModalDialogOptions) {
      options = nextOptions;
    },
    destroy() {
      node.removeEventListener("keydown", keydown);
      node.removeEventListener("cancel", cancel);
      node.removeEventListener("pointerdown", pointerdown);
      node.removeEventListener("click", click);
      node.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus();
      }
    },
  };
}
